import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// kind: 'residents' | 'banned'
const ImportSpreadsheet = ({ kind = 'residents', onImported }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const label = kind === 'banned' ? 'Banned / Restricted List' : 'Residents';

  const reset = () => { setStep('upload'); setPreview(null); setResult(null); setBusy(false); };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/import/${kind}/template`);
      const blob = new Blob([res.data.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download template'); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/import/${kind}/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setStep('preview');
      if (res.data.missing_columns?.length) {
        toast.warning(`Missing required column(s): ${res.data.missing_columns.join(', ')}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read that file');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commit = async () => {
    const rows = preview.rows.filter(r => r.status === 'ok').map(r => r.data);
    if (rows.length === 0) { toast.error('No valid rows to import'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/import/${kind}/commit`, { rows });
      setResult(res.data);
      setStep('done');
      toast.success(`Imported ${res.data.created} record(s)`);
      if (onImported) onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  const statusBadge = (s) => {
    if (s === 'ok') return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Ready</Badge>;
    if (s === 'duplicate') return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Duplicate — skipped</Badge>;
    return <Badge className="bg-red-100 text-red-800 border border-red-200">Error</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center space-x-2" data-testid={`import-${kind}-btn`}>
          <Upload className="h-4 w-4" />
          <span>Import</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-indigo-600" /> Import {label} from Spreadsheet
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4" data-testid="import-upload-step">
            <p className="text-sm text-gray-600">
              Upload a <strong>CSV or Excel</strong> file (.csv, .xlsx, .xls). We'll match your columns automatically,
              show you a preview, and flag any errors or duplicates before anything is saved.
            </p>
            <button onClick={downloadTemplate} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm font-medium" data-testid="download-template-btn">
              <Download className="h-4 w-4" /> Download the template (recommended column layout)
            </button>
            <label className="block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} data-testid="import-file-input" />
              {busy ? (
                <div className="flex flex-col items-center gap-2 text-gray-500"><Loader2 className="h-6 w-6 animate-spin" /> Reading file...</div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-500">
                  <Upload className="h-8 w-8" />
                  <span className="font-medium text-gray-700">Click to choose a file</span>
                  <span className="text-xs">CSV or Excel (.xlsx) · up to 25 MB · older .xls: save as .xlsx first</span>
                </div>
              )}
            </label>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4" data-testid="import-preview-step">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge className="bg-gray-100 text-gray-700 border">{preview.summary.total} rows</Badge>
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">{preview.summary.ok} ready</Badge>
              <Badge className="bg-amber-100 text-amber-800 border border-amber-200">{preview.summary.duplicate} duplicate</Badge>
              <Badge className="bg-red-100 text-red-800 border border-red-200">{preview.summary.error} error</Badge>
            </div>
            <div className="border rounded-lg overflow-auto max-h-[45vh]">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium text-gray-600">#</th>
                    <th className="text-left p-2 font-medium text-gray-600">Name</th>
                    <th className="text-left p-2 font-medium text-gray-600">DOB</th>
                    <th className="text-left p-2 font-medium text-gray-600">Status</th>
                    <th className="text-left p-2 font-medium text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`import-row-${r.status}`}>
                      <td className="p-2 text-gray-400">{r.row}</td>
                      <td className="p-2">{r.data.first_name} {r.data.last_name}</td>
                      <td className="p-2 text-gray-600">{r.data.date_of_birth || '—'}</td>
                      <td className="p-2">{statusBadge(r.status)}</td>
                      <td className="p-2 text-xs text-red-600">{r.errors?.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center">
              <Button variant="ghost" onClick={reset} disabled={busy}>Choose a different file</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={commit} disabled={busy || preview.summary.ok === 0} data-testid="import-commit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Import {preview.summary.ok} record{preview.summary.ok === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-4 text-center py-4" data-testid="import-done-step">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <div>
              <p className="text-lg font-semibold text-gray-900">Import complete</p>
              <p className="text-sm text-gray-600">
                {result.created} added · {result.skipped} skipped (duplicates){result.errors?.length ? ` · ${result.errors.length} errors` : ''}
              </p>
            </div>
            {result.errors?.length > 0 && (
              <div className="text-left text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2 max-h-32 overflow-auto">
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setOpen(false); reset(); }}>Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportSpreadsheet;
